import React, { useState, useEffect } from 'react';
import { X, User, Zap, ExternalLink, DollarSign } from 'lucide-react';
import { toast } from 'react-toastify';
import { useContractServices } from '../../../context/ContractServicesContext';
import { taskAPI } from '../../../services/api';

/**
 * A modal component for releasing Innovation Units to contributors
 */
const ReleaseIUModal = ({ 
  project, 
  task,
  submission,
  isOpen, 
  onClose, 
  onRelease,
  getExplorerUrl
}) => {
  // Contract services context
  const { services, isReady } = useContractServices();
  
  // State
  const [loading, setLoading] = useState(false);
  const [remainingContributorAllocation, setRemainingContributorAllocation] = useState(null);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [transactionHash, setTransactionHash] = useState(null);
  
  // Define a fallback getExplorerUrl in case it's not provided
  const safeGetExplorerUrl = (txHash) => {
    if (typeof getExplorerUrl === 'function') {
      return getExplorerUrl(txHash);
    }
    // Default to Optimism Sepolia as fallback
    const network = 'sepolia-optimism';
    return `https://${network}.etherscan.io/tx/${txHash}`;
  };
  
  // Fetch remaining contributor allocation when modal opens
  useEffect(() => {
    if (isOpen && isReady && project && services?.innovationUnits) {
      fetchRemainingContributorAllocation();
    }
  }, [isOpen, isReady, project, services]);
  
  // Function to fetch remaining contributor allocation
  const fetchRemainingContributorAllocation = async () => {
    if (!isReady || !services?.innovationUnits || !project?.projectTokenId) return;
    
    try {
      setLoadingAllocation(true);
      const projectId = parseInt(project.projectTokenId);
      const remaining = await services.innovationUnits.getRemainingContributorAllocation(projectId);
      setRemainingContributorAllocation(remaining.toString());
    } catch (error) {
      console.error("Error fetching remaining contributor allocation:", error);
    } finally {
      setLoadingAllocation(false);
    }
  };
  
  // Calculate percentage of total IU supply
  const calculatePercentage = () => {
    if (!task?.taskPoints || !project?.totalInnovationUnits) return "0";
    return ((task.taskPoints / project.totalInnovationUnits) * 100).toFixed(2);
  };
  
  // Handle the release confirmation
  const handleReleaseConfirm = async () => {
    if (!isReady) {
      toast.error("Smart contract services are not ready. Please connect your wallet and try again.");
      return;
    }
    
    if (!task || !submission || !project) {
      toast.error("Missing task or contributor information");
      return;
    }
    
    try {
      setLoading(true);
      
      console.log("Starting handleReleaseConfirm");
      console.log("Submission data:", submission);
      
      // Get the project ID and contributor wallet address
      const projectId = parseInt(project.projectTokenId);
      
      // Try both possible structures for the wallet address
      let contributorAddress = submission.contributor?.walletAddress;
      
      // If not found in the nested structure, try the direct property
      if (!contributorAddress && submission.contributorWalletAddress) {
        contributorAddress = submission.contributorWalletAddress;
      }
      
      const amount = task.taskPoints;
      
      console.log("Release parameters:", { 
        projectId, 
        contributorAddress, 
        amount 
      });
      
      // Validate contributor address
      if (!contributorAddress) {
        throw new Error("Contributor wallet address is missing or undefined");
      }
      
      // Additional validation to make sure the address is in the correct format
      if (typeof contributorAddress !== 'string' || !contributorAddress.startsWith('0x')) {
        throw new Error(`Invalid wallet address format: ${contributorAddress}`);
      }
      
      console.log(`Minting ${amount} IUs to contributor ${contributorAddress} for project ${projectId}`);
      
      // Call the smart contract function
      const result = await services.innovationUnits.mintToContributor(
        projectId,
        contributorAddress,
        amount
      );
      
      console.log("Transaction result:", result);
      
      // Store transaction hash for linking to explorer
      if (result && result.hash) {
        setTransactionHash(result.hash);
        
        // Update the task in the database to mark IUs as released
        await taskAPI.releaseIUs(task._id, {
          transactionHash: result.hash
        });
      }
      
      // Update the UI to show successful transaction
      toast.success(
        <div>
          Successfully minted {amount} IUs to contributor!
          <a 
            href={safeGetExplorerUrl(result.hash)} 
            target="_blank"
            rel="noopener noreferrer"
            className="block text-blue-600 underline mt-1 text-sm"
          >
            View transaction <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </div>
      );
      
      // Call the parent component's onRelease function to update UI state
      if (onRelease) {
        await onRelease(task._id, submission.contributor?._id || submission._id);
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error("Error releasing IUs:", error);
      toast.error(`Failed to release IUs: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Release Innovation Units</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Project and Task Information */}
        <div className="mb-4">
          <h3 className="font-semibold text-lg mb-2">{project?.title || 'Project'}</h3>
          <div className="bg-blue-50 p-3 rounded mb-3">
            <p className="text-sm mb-1"><span className="font-medium">Task:</span> {task?.title}</p>
            <p className="text-sm mb-1"><span className="font-medium">Status:</span> {task?.status?.toUpperCase()}</p>
            <p className="text-sm"><span className="font-medium">Points:</span> {task?.taskPoints} IUs</p>
          </div>
        </div>
        
        {/* Contributor Information */}
        <div className="mb-4">
          <h3 className="font-semibold text-md mb-2">Contributor Information</h3>
          <div className="bg-gray-50 p-3 rounded flex items-start gap-3">
            <User className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <p className="font-medium">{submission?.contributor?.name || 'Contributor'}</p>
              <p className="text-xs text-gray-500 break-all">{submission?.contributor?.walletAddress}</p>
              <p className="text-xs text-gray-500 mt-1">Submission Date: {submission ? formatDate(submission.submissionDate) : 'N/A'}</p>
            </div>
          </div>
        </div>
        
        {/* Allocation Information */}
        <div className="mb-4">
          <h3 className="font-semibold text-md mb-2">Allocation Information</h3>
          <div className="bg-indigo-50 p-3 rounded">
            {remainingContributorAllocation !== null ? (
              <>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Remaining Contributor Allocation:</span>
                  <span className="text-sm">{remainingContributorAllocation} IUs</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">IUs to Release:</span>
                  <span className="text-sm">{task?.taskPoints} IUs ({calculatePercentage()}% of total)</span>
                </div>
                {parseInt(remainingContributorAllocation) < task?.taskPoints && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                    <p className="font-medium">Warning: Insufficient Allocation</p>
                    <p>The remaining contributor allocation ({remainingContributorAllocation} IUs) is less than the task points ({task?.taskPoints} IUs).</p>
                  </div>
                )}
              </>
            ) : loadingAllocation ? (
              <div className="text-center py-2">
                <p className="text-sm text-gray-500">Loading allocation data...</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Could not load allocation data</p>
            )}
          </div>
        </div>
        
        {/* Transaction information - show if transaction submitted */}
        {transactionHash && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-sm mb-2 text-blue-800">Transaction Details</h4>
            <div className="flex items-center justify-between">
              <span className="text-xs text-blue-800">Transaction submitted</span>
              <a 
                href={safeGetExplorerUrl(transactionHash)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs flex items-center text-blue-600 hover:underline"
              >
                View on Explorer <ExternalLink className="h-3 w-3 ml-1" />
              </a>
            </div>
          </div>
        )}
        
        {/* Release Preview */}
        <div className="mb-6 p-3 bg-green-50 rounded">
          <h4 className="font-semibold mb-2 text-sm">Release Preview</h4>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Project:</span>
            <span className="font-medium">{project?.title || 'Project'}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Task:</span>
            <span className="font-medium">{task?.title}</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">Contributor:</span>
            <span className="font-medium">{submission?.contributor?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">IUs to Release:</span>
            <span className="font-medium">{task?.taskPoints} IUs</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleReleaseConfirm}
            disabled={
              loading || 
              (remainingContributorAllocation !== null && 
               parseInt(remainingContributorAllocation) < task?.taskPoints)
            }
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? 'Processing...' : (
              <>
                <DollarSign className="w-4 h-4 mr-2" />
                Confirm Release
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseIUModal; 